import { useState } from "react";
import { Scissors, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/lib/permissions";

/**
 * RequestTakedownButton — kicks off a Takedown Management flow from a
 * finding. Visible to admins and SOC analysts; hidden for client/read-only.
 *
 * Props:
 *   finding: full finding doc (needs .id and ideally .category/.signal)
 */
export function RequestTakedownButton({ finding }) {
  const { role } = usePermissions();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [priority, setPriority] = useState("medium");
  const [submitting, setSubmitting] = useState(false);

  const canRequest = role === "admin" || role === "soc_analyst";
  if (!canRequest) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/takedowns", {
        finding_id: finding.id,
        reason: reason.trim(),
        priority,
      });
      toast.success("Takedown creado · redirigiendo al módulo");
      setOpen(false);
      // Small delay so the user sees the toast before route change
      setTimeout(() => nav("/takedowns"), 800);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el takedown");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 border border-sev-high text-sev-high
                         px-3 py-1.5 text-xs font-mono uppercase tracking-widest
                         hover:bg-sev-high hover:text-background transition-colors"
              data-testid="request-takedown-btn">
        <Scissors className="w-3 h-3" />
        Request Takedown
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             onClick={() => setOpen(false)} data-testid="takedown-request-modal">
          <div onClick={e => e.stopPropagation()}
               className="bg-card border border-border max-w-lg w-full p-5 space-y-4">
            <h3 className="font-display text-lg font-medium flex items-center gap-2">
              <Scissors className="w-4 h-4 text-sev-high" />
              Solicitar Takedown
            </h3>
            <div className="text-xs text-muted-foreground">
              Se creará una solicitud de retirada vinculada al hallazgo{" "}
              <code className="font-mono text-primary">{finding.id?.slice(0, 8)}…</code>.
              La evidencia se autopobla con los metadatos actuales del finding.
            </div>

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                Prioridad
              </label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                      className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"
                      data-testid="takedown-request-priority">
                <option value="low">Baja</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
                <option value="critical">Crítica</option>
              </select>
            </div>

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                Justificación (opcional)
              </label>
              <textarea value={reason} onChange={e => setReason(e.target.value)}
                        rows={4}
                        placeholder="Ej. Página de phishing suplanta nuestra marca; registrador: GoDaddy."
                        className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none font-mono"
                        data-testid="takedown-request-reason" />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button onClick={() => setOpen(false)}
                      className="border border-border px-4 py-2 text-sm hover:border-muted-foreground">
                Cancelar
              </button>
              <button onClick={submit} disabled={submitting}
                      className="bg-sev-high text-background px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-50"
                      data-testid="takedown-request-submit">
                {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
                Crear Takedown
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
